import { v4 as uuidv4 } from 'uuid';
import {
  BlocoEmail,
  ModeloEmail,
  TipoBlocoEmail,
  VariavelEmail,
} from '../model/email/modelo-email.model';

const COR_BOTAO_PADRAO = '#993DF5';

function escaparHtml(valor: string | undefined): string {
  return (valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escaparAtributo(valor: string | undefined): string {
  return (valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function quebrasParaBr(valor: string | undefined): string {
  return escaparHtml(valor).replace(/\r?\n/g, '<br>');
}

function limitar(
  valor: number | undefined,
  min: number,
  max: number,
  padrao: number,
): number {
  const n = typeof valor === 'number' && !isNaN(valor) ? valor : padrao;
  return Math.min(max, Math.max(min, n));
}

export function criarBloco(tipo: TipoBlocoEmail): BlocoEmail {
  const base: BlocoEmail = { id: uuidv4(), tipo };
  switch (tipo) {
    case 'TITULO':
      return { ...base, texto: '' };
    case 'TEXTO':
      return { ...base, texto: '' };
    case 'IMAGEM':
      return { ...base, urlImagem: '', alinhamento: 'center', largura: 100 };
    case 'BOTAO':
      return {
        ...base,
        labelBotao: 'Clique aqui',
        urlBotao: '',
        corBotao: COR_BOTAO_PADRAO,
      };
    case 'VARIAVEL':
      return { ...base, texto: '' };
    case 'DIVISOR':
      return base;
    case 'HTML':
      return { ...base, html: '' };
  }
}

function alinhamentoSeguro(
  valor: string | undefined,
  padrao: 'left' | 'center' | 'right',
): 'left' | 'center' | 'right' {
  return valor === 'left' || valor === 'center' || valor === 'right'
    ? valor
    : padrao;
}

function renderizarBloco(bloco: BlocoEmail, identificarBloco: boolean): string {
  const alinhar = alinhamentoSeguro(bloco.alinhamento, 'left');
  const atributoBloco = identificarBloco
    ? ` data-email-block-id="${escaparAtributo(bloco.id)}"`
    : '';
  switch (bloco.tipo) {
    case 'TITULO':
      return `<tr${atributoBloco}><td style="padding:16px 24px 4px;text-align:${alinhar};"><h1 style="margin:0;font-size:22px;line-height:1.3;color:#1a1a1a;font-weight:700;font-family:Arial,Helvetica,sans-serif;">${escaparHtml(bloco.texto)}</h1></td></tr>`;
    case 'TEXTO':
      return `<tr${atributoBloco}><td style="padding:8px 24px;font-size:15px;line-height:1.55;color:#333333;font-family:Arial,Helvetica,sans-serif;text-align:${alinhar};">${quebrasParaBr(bloco.texto)}</td></tr>`;
    case 'VARIAVEL':
      return `<tr${atributoBloco}><td style="padding:8px 24px;font-size:15px;line-height:1.55;color:#333333;font-family:Arial,Helvetica,sans-serif;text-align:${alinhar};">${escaparHtml(bloco.texto)}</td></tr>`;
    case 'IMAGEM': {
      const largura = limitar(bloco.largura, 10, 100, 100);
      const alinhamento = alinhamentoSeguro(bloco.alinhamento, 'center');
      const src = escaparAtributo(bloco.urlImagem);
      if (!src) {
        return `<tr${atributoBloco}><td style="padding:0;"><div style="height:120px;background:#ece7f4;color:#8a7fb0;font:italic 13px Arial;display:flex;align-items:center;justify-content:center;">Imagem</div></td></tr>`;
      }
      return `<tr${atributoBloco}><td align="${alinhamento}" style="padding:0;"><img src="${src}" alt="" style="display:block;width:${largura}%;max-width:100%;border:0;outline:none;text-decoration:none;" /></td></tr>`;
    }
    case 'BOTAO': {
      const cor = escaparAtributo(bloco.corBotao || COR_BOTAO_PADRAO);
      const url = escaparAtributo(bloco.urlBotao || '#');
      return `<tr${atributoBloco}><td align="${alinhar}" style="padding:16px 24px;text-align:${alinhar};"><a href="${url}" target="_blank" style="display:inline-block;background:${cor};color:#ffffff;text-decoration:none;font-weight:700;font-size:15px;font-family:Arial,Helvetica,sans-serif;padding:12px 28px;border-radius:8px;">${escaparHtml(bloco.labelBotao)}</a></td></tr>`;
    }
    case 'DIVISOR':
      return `<tr${atributoBloco}><td style="padding:8px 24px;"><hr style="border:0;border-top:1px solid #e5e5e5;margin:0;" /></td></tr>`;
    case 'HTML':
      return `<tr${atributoBloco}><td style="padding:0;text-align:${alinhar};">${bloco.html ?? ''}</td></tr>`;
  }
}

export function montarHtmlEmail(
  modelo: ModeloEmail | null | undefined,
  variaveisPreview: VariavelEmail[] = [],
  identificarBlocosPreview = false,
): string {
  const blocos = modelo?.blocos ?? [];
  const htmlImportado =
    blocos.length === 1 &&
    blocos[0].tipo === 'HTML' &&
    /<!doctype\s+html|<html[\s>]/i.test(blocos[0].html ?? '');
  if (htmlImportado) {
    return substituirVariaveisEmail(blocos[0].html ?? '', variaveisPreview);
  }

  const corpo = blocos
    .map((bloco) => renderizarBloco(bloco, identificarBlocosPreview))
    .join('');
  const doc =
    `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1"></head>` +
    `<body style="margin:0;padding:0;background:#ffffff;">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;background:#ffffff;">` +
    `<tr><td align="center" style="padding:0;" valign="top">` +
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;background:#ffffff;border:1px solid #ececf1;border-radius:12px;overflow:hidden;box-shadow:0 1px 4px rgba(16,24,40,.06);border-collapse:separate;">` +
    corpo +
    `</table></td></tr></table></body></html>`;
  return substituirVariaveisEmail(doc, variaveisPreview);
}

interface ContatoPreviewEmail {
  nome?: string;
  nomeContato?: string;
  email?: string;
  telefone?: string;
  cpf?: string;
  camposComplementares?:
    | Record<string, unknown>
    | { nome?: string; nomeColuna?: string; valor: unknown }[];
}

export function criarVariaveisEmail(
  contato: ContatoPreviewEmail | null | undefined,
  colunasComplementares: string[] = [],
): VariavelEmail[] {
  if (!contato) {
    return [];
  }

  const variaveis = [
    new VariavelEmail(
      'nome',
      String(contato.nomeContato ?? contato.nome ?? ''),
    ),
    new VariavelEmail('email', String(contato.email ?? '')),
    new VariavelEmail('telefone', String(contato.telefone ?? '')),
    new VariavelEmail('cpf', String(contato.cpf ?? '')),
  ];
  const nomesFixos = new Set(
    variaveis.map((variavel) => normalizarNomeVariavel(variavel.nome)),
  );

  colunasComplementares
    .filter(
      (nome) => !!nome?.trim() && !nomesFixos.has(normalizarNomeVariavel(nome)),
    )
    .filter(
      (nome, indice, lista) =>
        lista.findIndex(
          (item) =>
            normalizarNomeVariavel(item) === normalizarNomeVariavel(nome),
        ) === indice,
    )
    .forEach((nome) =>
      variaveis.push(
        new VariavelEmail(nome, buscarValorComplementar(contato, nome)),
      ),
    );

  return variaveis;
}

export function substituirVariaveisEmail(
  texto: string | undefined,
  variaveis: VariavelEmail[],
): string {
  if (!texto || !variaveis.length) {
    return texto ?? '';
  }

  const valores = new Map(
    variaveis.map((variavel) => [
      normalizarNomeVariavel(variavel.nome),
      variavel.valor ?? '',
    ]),
  );
  return texto.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (token, nome: string) => {
    const chave = normalizarNomeVariavel(nome);
    return valores.has(chave) ? valores.get(chave)! : token;
  });
}

function buscarValorComplementar(
  contato: ContatoPreviewEmail,
  nome: string,
): string {
  const campos = contato.camposComplementares;
  if (Array.isArray(campos)) {
    const campo = campos.find(
      (item) =>
        normalizarNomeVariavel(item.nome ?? item.nomeColuna ?? '') ===
        normalizarNomeVariavel(nome),
    );
    return String(campo?.valor ?? '');
  }
  if (campos && typeof campos === 'object') {
    const chave = Object.keys(campos).find(
      (item) => normalizarNomeVariavel(item) === normalizarNomeVariavel(nome),
    );
    return chave ? String(campos[chave] ?? '') : '';
  }
  return '';
}

export function normalizarNomeVariavel(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}
